import { Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { OfflineBanner } from '../components/system-states/OfflineBanner';
import { ToneAreaProvider } from '../components/ToneArea';
import { ImpersonationBanner } from '../features/auth/components/ImpersonationBanner';
import { isScopeNonDefault, useSession } from '../stores/session';
import { CommandPalette } from './CommandPalette';
import { ModuleBar } from './ModuleBar';
import { ModuleRouteGate } from './ModuleRouteGate';
import { SectionTone } from './section-tone';
import { ShortcutSheet } from './ShortcutSheet';
import { UtilityBar } from './UtilityBar';
import { useGlobalShortcuts } from './use-global-shortcuts';
import styles from './AppShell.module.css';

// SYS-08 — the three-level global shell.
//
// CLAUDE.md §5a rule 6: this frame mounts once and stays mounted. Rail, header and
// notification centre never re-render on navigation; only `<Outlet />` changes. A
// route transition is therefore a component swap, never a fetch — which is also
// why there is no route-level code splitting (rule 5).

export function AppShell(): ReactNode {
  useGlobalShortcuts();

  const scope = useSession((state) => state.scope);
  const hasSession = useSession((state) => state.session !== null);

  // Everything inside assumes a signed-in viewer (`useRequiredSession`). The gate
  // keeps that true on the way in; this keeps it true on the way out, for the one
  // render where a session has ended but the sign-in screen has not yet replaced
  // the shell (D8).
  if (!hasSession) return null;

  return (
    <div className={styles.shell}>
      <div className={styles.banners}>
        {/* F1.10: the impersonation banner is above everything, always visible,
            and cannot be scrolled away from. */}
        <ImpersonationBanner />
        <OfflineBanner />
      </div>

      {/* The scope indicator's second signal: an accent rule across the whole
          header whenever scope is not the default (06-qol §8). */}
      <header className={styles.header} data-scope={isScopeNonDefault(scope) ? 'custom' : 'default'}>
        <UtilityBar />
        <ModuleBar />
      </header>

      <main className={styles.main}>
        <ToneAreaProvider>
          <ModuleRouteGate>
            <Outlet />
          </ModuleRouteGate>
        </ToneAreaProvider>
      </main>

      <SectionTone />
      <CommandPalette />
      <ShortcutSheet />
    </div>
  );
}

interface ContextualTabsProps {
  readonly tabs: readonly {
    readonly id: string;
    readonly label: string;
    readonly count?: number;
  }[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
}

/**
 * Level 3 — rendered by each record page rather than by the shell, because the
 * tabs belong to the record, not to the application.
 */
export function ContextualTabs({ tabs, activeId, onSelect }: ContextualTabsProps): ReactNode {
  return (
    <div className={styles.tabs} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === activeId}
          className={styles.tab}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <span className={styles.tabCount}>{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}
